"use client";

import { useRef } from "react";
import { PageCompetitionSchema } from "@/sanity/pages/PageCompetitionTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { useScroll, useTransform } from "framer-motion";

import bg from "@/public/global/bgCompetition.png";
import ChipSerif from "@/components/global/ui/ChipSerif";
import Countdown from "@/components/global/countdown/Countdown";
import doubleArrow from "@/public/global/svg/doubleArrow.svg";

export default function WorkflowSection({
  data,
  className,
}: {
  data: PageCompetitionSchema;
  className?: string;
}) {

  return (
    <section
      id="hero-section"
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[3rem] pt-[4rem]",
        className,
      )}
    >
      <div className="relative overflow-clip w-full mx-auto rounded-[1.5rem] lg:rounded-[5rem] py-[5.88rem] lg:pt-[14.25rem] lg:pb-[20.75rem]">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !max-w-none !w-full !h-full object-cover object-top mix-blend-luminosity"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container flex relative gap-[3.25rem] lg:gap-[6.12rem] z-10 flex-col items-center px-[3rem]">
          <div className="flex flex-col items-center gap-[1.5rem]">
            {data?.workflowChip && (
              <ChipSerif text={data.workflowChip} className="mb-0" />
            )}

            {data?.workflowText && (
              <div className="richtext prose-h2:text-[4rem] lg:prose-h2:text-[6rem]">
                <PortableText
                  value={data.workflowText}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          <div className="flex lg:flex-row flex-col items-center gap-[2.5rem] w-full lg:w-[min(148rem,100%)]">
            <div className="flex flex-col items-center gap-[4.5rem] bg-white rounded-[3rem] p-[2.25rem]">
              <div className="w-full h-[32.375rem] rounded-[1.5rem] overflow-hidden relative">
                <Image src={data.workflowCardImage1} alt="workflow card 1" quality={100} className="object-cover !w-full !h-full" />
              </div>

              {data?.workflowCardText1 && (
                <div className="relative prose-h2:text-[2.75rem] prose-h2:leading-none prose-h2:tracking-[-0.0825rem] prose-h2:capitalize prose-p:text-[2rem] prose-p:leading-[140%] prose-p:text-[#505050] prose-p:mb-0 m-[2rem] mt-0">
                  <span className="absolute top-[-7.25rem] left-0 text-white py-[1.25rem] px-[2rem] rounded-[1.25rem] text-[2.25rem] leading-none font-[600] bg-[#608AA9]">01</span>
                  <PortableText 
                    value={data.workflowCardText1}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col items-center gap-[4.5rem] bg-white rounded-[3rem] p-[2.25rem]">
              <div className="w-full h-[32.375rem] rounded-[1.5rem] overflow-hidden">
                <Image src={data.workflowCardImage2} alt="workflow card 2" quality={100} className="object-cover !w-full !h-full" />
              </div>

              {data?.workflowCardText2 && (
                <div className="relative prose-h2:text-[2.75rem] prose-h2:leading-none prose-h2:tracking-[-0.0825rem] prose-h2:capitalize prose-p:text-[2rem] prose-p:leading-[140%] prose-p:text-[#505050] prose-p:mb-0 m-[2rem] mt-0">
                  <span className="absolute top-[-7.25rem] left-0 text-[#103037] py-[1.25rem] px-[2rem] rounded-[1.25rem] text-[2.25rem] leading-none font-[600] bg-[#85D0A6]">02</span>
                  <PortableText
                    value={data.workflowCardText2}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col items-center gap-[4.5rem] bg-white rounded-[3rem] p-[2.25rem]">
              <div className="w-full h-[32.375rem] rounded-[1.5rem] overflow-hidden">
                <Image src={data.workflowCardImage3} alt="workflow card 3" quality={100} className="object-cover !w-full !h-full" />
              </div>

              {data?.workflowCardText3 && (
                <div className="relative prose-h2:text-[2.75rem] prose-h2:leading-none prose-h2:tracking-[-0.0825rem] prose-h2:capitalize prose-p:text-[2rem] prose-p:leading-[140%] prose-p:text-[#505050] prose-p:mb-0 m-[2rem] mt-0">
                  <span className="absolute top-[-7.25rem] left-0 text-[#103037] py-[1.25rem] px-[2rem] rounded-[1.25rem] text-[2.25rem] leading-none font-[600] bg-[#F4D377]">03</span>
                  <PortableText
                    value={data.workflowCardText3}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
          </div>

          {data?.workflowCTA && (
            <CTAButton className="" data={data.workflowCTA} />
          )}
        </div>
      </div>
    </section>
  );
}
